import React, { useState, useEffect, useRef } from 'react';
import { launchConfettiOverBox } from './utils/confetti';

// Quiz-Komponente zur Anzeige von Fragen und Bearbeitung von Antworten.
// React.memo wird verwendet, um unnötige Neu-Renderings zu vermeiden,
// wenn sich die Props nicht ändern.
const Quiz = React.memo(({ quizData }) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null); // Speichert den Index der aktuell ausgewählten Antwort.
  const [feedbackMessage, setFeedbackMessage] = useState(''); // Speichert die Feedback-Nachricht (Richtig/Falsch).
  const [isCorrect, setIsCorrect] = useState(false); // Verfolgt, ob die richtige Antwort gefunden wurde.
  const [attemptedWrongIndexes, setAttemptedWrongIndexes] = useState([]); // Speichert die Indizes der falsch versuchten Antworten.
  const quizContainerRef = useRef(null); // Referenz auf das DOM-Element des Quiz-Containers.
  const canvasRef = useRef(null); // Referenz auf das DOM-Element des Canvas.

  // Setzt den Quiz-Zustand zurück, wenn sich die Quiz-Daten (z.B. bei Kapitelwechsel) ändern.
  useEffect(() => {
    setSelectedOptionIndex(null);
    setFeedbackMessage('');
    setIsCorrect(false);
    setAttemptedWrongIndexes([]); // Setzt die Liste der falschen Versuche zurück.
  }, [quizData]); // Abhängigkeit von quizData stellt sicher, dass der Reset beim Kapitelwechsel erfolgt.

  // Startet Konfetti, wenn die Antwort korrekt ist und das Canvas-Element verfügbar ist.
  useEffect(() => {
    if (isCorrect && canvasRef.current) { // Prüft, ob isCorrect true ist UND Canvas-Element existiert.
      launchConfettiOverBox(canvasRef.current); // Canvas-Element direkt übergeben.
    }
  }, [isCorrect]); // Dieser useEffect reagiert nur auf Änderungen in isCorrect.

  // Behandelt das Klicken auf eine Antwortoption.
  const handleOptionClick = (index) => {
    if (isCorrect) return; // Wenn die Antwort bereits korrekt ist, verhindere weitere Klicks.
    setSelectedOptionIndex(index);
    setFeedbackMessage(''); // Löscht vorheriges Feedback, wenn eine neue Option ausgewählt wird.
  };

  // Behandelt das Absenden der Antwort.
  const handleSubmit = () => {
    if (selectedOptionIndex === null) {
      setFeedbackMessage('Bitte wähle eine Antwort aus.');
      return;
    }

    if (selectedOptionIndex === quizData.correctAnswerIndex) {
      setFeedbackMessage('Richtig! Gut gemacht!');
      setIsCorrect(true); // Setzt den Zustand auf korrekt, was den useEffect oben triggert.
    } else {
      setFeedbackMessage('Falsch. Bitte versuche es noch einmal.');
      // Fügt die aktuell ausgewählte (falsche) Option zur Liste der falschen Versuche hinzu.
      setAttemptedWrongIndexes(prev => [...prev, selectedOptionIndex]);
      setSelectedOptionIndex(null); // Löscht die Auswahl für den nächsten Versuch.
    }
  };

  return (
    <div ref={quizContainerRef} className="bg-[#Faf6f4] p-6 rounded-lg shadow-inner mt-4 relative overflow-hidden border border-gray-200">
      <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full pointer-events-none"></canvas>
      <h4 className="text-xl text-gray-800 mb-4 font-open-sans font-normal">{quizData.question}</h4> {/* Wendet die Open Sans-Schriftart auf die Frage an. */}
      <div className="space-y-3">
        {quizData.options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleOptionClick(index)}
            className={`w-full text-left p-3 rounded-md border-2 transition-colors duration-300 font-open-sans font-normal
              ${selectedOptionIndex === index && !isCorrect ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-white'} /* Styling für ausgewählte Antwort (vor dem Absenden). */
              ${isCorrect && index === quizData.correctAnswerIndex ? 'bg-green-100 border-green-500 ring-4 ring-green-300' : ''} /* Styling für die korrekte Antwort nach dem Absenden. */
              ${!isCorrect && attemptedWrongIndexes.includes(index) ? 'bg-red-100 border-red-500' : ''} /* Styling für falsch versuchte Antworten. */
              ${isCorrect ? 'cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'} /* Cursor und Hover-Effekt. */
            `}
            disabled={isCorrect} // Deaktiviert alle Buttons, sobald die richtige Antwort gefunden wurde.
          >
            {option}
          </button>
        ))}
      </div>
      {!isCorrect && ( // Zeigt den Absende-Button nur an, wenn die Antwort noch nicht korrekt ist.
        <button
          onClick={handleSubmit}
          className="mt-6 px-6 py-3 rounded-full transition-colors shadow-md font-open-sans bg-[#008080] text-white hover:bg-[#006666] focus:outline-none focus:ring-2 focus:ring-[#008080] focus:ring-opacity-50"
        >
          Antwort absenden
        </button>
      )}
      {feedbackMessage && ( // Zeigt die Feedback-Nachricht an.
        <p className={`mt-4 text-lg font-open-sans ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
          {feedbackMessage}
        </p>
      )}
    </div>
  );
}); // Ende von React.memo() für die Quiz-Komponente.

export default Quiz;